import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const MarketingMetrics = ({ dateRange, metrics }) => {
  const chartData = metrics?.map(m => ({
    date: new Date(m?.metricDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    visits: m?.websiteVisits,
    bookings: m?.onlineBookings,
    conversions: m?.conversionRate
  })) || [];

  const latestMetric = metrics?.[metrics?.length - 1] || {};
  const previousMetric = metrics?.[metrics?.length - 2] || {};

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return (((current - previous) / previous) * 100)?.toFixed(1);
  };

  const performanceMetrics = [
    {
      label: 'Website Conversions',
      value: `${latestMetric?.conversionRate || 0}%`,
      change: calculateChange(latestMetric?.conversionRate, previousMetric?.conversionRate),
      trend: latestMetric?.conversionRate >= previousMetric?.conversionRate ? 'up' : 'down',
      icon: 'MousePointerClick',
      color: 'bg-primary/10 text-primary'
    },
    {
      label: 'Online Bookings',
      value: latestMetric?.onlineBookings || 0,
      change: calculateChange(latestMetric?.onlineBookings, previousMetric?.onlineBookings),
      trend: latestMetric?.onlineBookings >= previousMetric?.onlineBookings ? 'up' : 'down',
      icon: 'Calendar',
      color: 'bg-success/10 text-success'
    },
    {
      label: 'Patient Acquisition Cost',
      value: `$${latestMetric?.patientAcquisitionCost || 0}`,
      change: calculateChange(previousMetric?.patientAcquisitionCost, latestMetric?.patientAcquisitionCost),
      trend: latestMetric?.patientAcquisitionCost <= previousMetric?.patientAcquisitionCost ? 'up' : 'down',
      icon: 'DollarSign',
      color: 'bg-warning/10 text-warning'
    },
    {
      label: 'Marketing ROI',
      value: `${latestMetric?.roiPercentage || 0}%`,
      change: calculateChange(latestMetric?.roiPercentage, previousMetric?.roiPercentage),
      trend: latestMetric?.roiPercentage >= previousMetric?.roiPercentage ? 'up' : 'down',
      icon: 'TrendingUp',
      color: 'bg-muted text-muted-foreground'
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          Marketing Performance Metrics
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {performanceMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${metric?.color}`}>
                <Icon name={metric?.icon} size={20} />
              </div>
              <div className="flex items-center gap-1">
                <Icon
                  name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'}
                  size={14}
                  className={metric?.trend === 'up' ? 'text-success' : 'text-error'}
                />
                <span className={`text-xs font-semibold ${metric?.trend === 'up' ? 'text-success' : 'text-error'}`}>
                  {metric?.change}%
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-1">{metric?.label}</p>
            <p className="text-2xl font-bold text-foreground">{metric?.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Performance Trends</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="visits"
                stroke="var(--color-primary)"
                fill="var(--color-primary)"
                fillOpacity={0.2}
                name="Website Visits"
              />
              <Area
                type="monotone"
                dataKey="bookings"
                stroke="var(--color-success)"
                fill="var(--color-success)"
                fillOpacity={0.2}
                name="Online Bookings"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MarketingMetrics;